import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Link from 'components/common/Link';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import InputAdornment from '@material-ui/core/InputAdornment';
import TextField, { TextFieldAdornmentText } from 'components/fields/TextField';
import CheckboxField from 'components/fields/CheckboxField';
import IconCheck from 'components/icons/IconCheck';
import IconCrossBold from 'components/icons/IconCrossBold';

import FormError from 'components/common/FormError';

import { accountSignUp } from 'redux/modules/account';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import passwordRules from 'config/passwordRules';

import pxToRem from 'styles/pxToRem';

const styles = theme => ({
  grid: {
    marginTop: 15,
    '&:first-child': {
      marginTop: 0,
    },
  },
  error: {
    marginTop: 24,
  },
  typography: {
    textTransform: 'uppercase',
    textAlign: 'center',
    paddingBottom: 14,
    marginBottom: 24,
    marginTop: 24,
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
  },
  trapField: {
    position: 'absolute',
    top: 0,
    left: 0,
    opacity: 0,
    zIndex: -1,
    width: 100,
    pointerEvents: 'none',
  },
  typographyText: {
    fontSize: pxToRem(13),
    margin: '16px auto 8px',
  },
  iconCrossBold: {
    width: 10,
    height: 10,
    color: theme.palette.validation[500],
    marginRight: 2,
  },
  iconCheck: {
    width: 12,
    height: 12,
    color: theme.palette.active[500],
    marginRight: 0,
  },
  listItem: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  listItemTextInvalid: {
    color: theme.palette.validation[500],
  },
});

function onSubmit(values, dispatch) {
  if (values['Repeat Email']) {
    throw new SubmissionError({
      _error: 'Spam Rejected',
    });
  }
  const normalizedValues = Object.assign({}, values);
  delete normalizedValues.Accept;

  return new Promise((resolve, reject) => {
    dispatch(accountSignUp({
      values: normalizedValues,
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      throw new SubmissionError({
        _error: err.error,
      });
    });
}

function validateForm(values) {
  const errors = {};
  if (values.Password) {
    // eslint-disable-next-line no-underscore-dangle
    errors._error = [
      ...Boolean(validate.containsUppercaseAlphabetical(values.Password)) && [
        'containsUppercaseAlphabetical',
      ],
      ...Boolean(validate.containsLowercaseAlphabetical(values.Password)) && [
        'containsLowercaseAlphabetical',
      ],
      ...Boolean(validate.containsNumber(values.Password)) && [
        'containsNumber',
      ],
      ...Boolean(validate.containsSpecialChar(values.Password)) && [
        'containsSpecialChar',
      ],
      ...Boolean(validate.minLength8(values.Password)) && [
        'minLength8',
      ],
    ];

    // eslint-disable-next-line no-underscore-dangle
    if (errors._error.length === 0) {
      // eslint-disable-next-line no-underscore-dangle
      delete errors._error;
    }
  } else {
    errors._error = [
      ...Boolean(validate.containsUppercaseAlphabetical(' ')) && [
        'containsUppercaseAlphabetical',
      ],
      ...Boolean(validate.containsLowercaseAlphabetical(' ')) && [
        'containsLowercaseAlphabetical',
      ],
      ...Boolean(validate.containsNumber(' ')) && [
        'containsNumber',
      ],
      ...Boolean(validate.containsSpecialChar(' ')) && [
        'containsSpecialChar',
      ],
      ...Boolean(validate.minLength8(' ')) && [
        'minLength8',
      ],
    ];
  }
  if (values.Password && values['Repeat Password']) {
    if (values.Password !== values['Repeat Password']) {
      errors['Repeat Password'] = 'Passwords do not match';
    } else {
      errors['Repeat Password'] = validate.password(values['Repeat Password']);
    }
  } else if (values['Repeat Password']) {
    errors['Repeat Password'] = 'Please, fill Password field first';
  }
  if (!values.Accept) {
    errors.Accept = 'Please, accept the Terms of Use and Privacy Policy';
  }
  return errors;
}

const SignUpForm = ({ handleSubmit, error, dirty, className, classes, submitSucceeded, passwordValue }) => (
  <form
    className={className}
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
  >
    {error && typeof error !== 'object' &&
      <Grid
        container
        spacing={24}
      >
        <Grid
          item
          xs={12}
        >
          <FormError className={classes.error}>
            {error}
          </FormError>
        </Grid>
      </Grid>
    }
    {submitSucceeded ? (
      <Grid
        container
        spacing={24}
      >
        <Grid
          item
          xs={12}
        >
          <Typography
            className={classes.typography}
            color="secondary"
          >
            You are on your way to getting your DME business seamless
          </Typography>
          <Typography variant="subheading" align="center">
            We value security. <br />
            A confirmation email was sent. All you have to do is click on the link.
          </Typography>
        </Grid>
      </Grid>
    ) : (
      <Fragment>
        <Field
          className={classes.trapField}
          id="Repeat Email"
          name="Repeat Email"
          placeholder="Repeat Email"
          component={TextField}
          size="lg"
          type="email"
          autoComplete="new-password"
          fullWidth
        />
        <Typography
          className={classes.typography}
          color="secondary"
        >
          Create Your Free Account
        </Typography>
        <Grid
          className={classes.grid}
          container
          spacing={24}
        >
          <Grid
            item
            xs={12}
          >
            <Field
              id="Email"
              name="Email"
              label="Contact Person Email"
              placeholder="Enter Email"
              component={TextField}
              validate={[validate.required, validate.email]}
              size="lg"
              type="email"
              autoComplete="new-password"
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid
          className={classes.grid}
          container
          spacing={24}
        >
          <Grid
            item
            xs={12}
          >
            <Field
              id="Phone Number"
              name="Phone Number"
              label="Mobile Phone Number"
              component={TextField}
              validate={[validate.required, validate.phone]}
              format={value => (value ? value.replace('+1 ', '') : '')}
              normalize={normalize.phone}
              maxLength="14"
              startAdornment={
                <InputAdornment position="start">
                  <TextFieldAdornmentText>
                    +1
                  </TextFieldAdornmentText>
                </InputAdornment>
              }
              size="lg"
              fullWidth
            />
          </Grid>
        </Grid>
        {(passwordValue && passwordValue.length > 0) &&
          <Fragment>
            <Typography
              className={classes.typographyText}
              align="center"
            >
              Password must contain the following:
            </Typography>
            <List disablePadding>
              {passwordRules.map(item => (
                <ListItem
                  className={classes.listItem}
                  key={item.text}
                  disableGutters
                  dense
                >
                  <ListItemIcon>
                    {error && error.indexOf(item.name) !== -1 ? (
                      <IconCrossBold className={classes.iconCrossBold} />
                    ) : (
                      <IconCheck className={classes.iconCheck} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    classes={{
                      primary: classNames({
                        [classes.listItemTextInvalid]: item.invalid,
                      }),
                    }}
                    primary={item.text}
                  />
                </ListItem>
              ))}
            </List>
          </Fragment>
        }
        <Grid
          className={classes.grid}
          container
          justify="center"
          spacing={24}
        >
          <Grid item xs={12}>
            <Field
              id="Password"
              name="Password"
              label="Password"
              component={TextField}
              validate={[
                validate.required,
                validate.noCyrillic,
                validate.password,
              ]}
              type="password"
              autoComplete="new-password"
              size="lg"
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid
          className={classes.grid}
          container
          justify="center"
          spacing={24}
        >
          <Grid item xs={12}>
            <Field
              id="Repeat Password"
              name="Repeat Password"
              label="Repeat Password"
              component={TextField}
              validate={[
                validate.required,
                validate.noCyrillic,
              ]}
              type="password"
              autoComplete="new-password"
              size="lg"
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid
          className={classes.grid}
          container
          justify="flex-start"
          spacing={24}
        >
          <Grid
            item
            xs={12}
          >
            <Field
              id="Accept"
              name="Accept"
              label={(
                <Fragment>
                  I accept the
                    <Link href="/terms-of-use" target="_blank" color="primary" underline>Terms of Use</Link> and {' '}
                    <Link href="/privacy-policy" target="_blank" color="primary" underline>Privacy Policy</Link>
                </Fragment>
              )}
              component={CheckboxField}
            />
          </Grid>
        </Grid>
      </Fragment>
    )}
  </form>
);

SignUpForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  className: PropTypes.string,
  passwordValue: PropTypes.string,
  error: PropTypes.string,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
    error: PropTypes.string.isRequired,
    trapField: PropTypes.string.isRequired,
  }).isRequired,
};

SignUpForm.defaultProps = {
  className: '',
  error: null,
  passwordValue: null,
  step: 1,
};

export default reduxForm({
  onSubmit,
  validate: validateForm,
})(withStyles(styles)(SignUpForm));



// WEBPACK FOOTER //
// ./src/components/account/SignUp/SignUpForm.js